/**
 * Authentication Context
 * Manages user authentication state
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import apiClient, { type ApiResponse } from '../lib/api-client';

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'ADMIN' | 'CUSTOMER';
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
  logout: () => void;
  setUser: (user: User, token: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          console.log('🔐 Attempting login for:', email);
          const response = await apiClient.post<ApiResponse<{ user: User; accessToken: string }>>(
            '/auth/login',
            { email, password }
          );

          console.log('✅ Login response:', response.data);
          const { user, accessToken } = response.data.data;

          // Store token in localStorage for axios interceptor
          localStorage.setItem('authToken', accessToken);
          localStorage.setItem('user', JSON.stringify(user));

          set({
            user,
            token: accessToken,
            isAuthenticated: true,
            isLoading: false,
          });
          console.log('✅ Login successful, auth state updated');
        } catch (error: any) {
          console.error('❌ Login error:', error);
          console.error('Error response:', error.response?.data);
          console.error('Error status:', error.response?.status);
          set({ isLoading: false });
          throw error;
        }
      },

      loginWithGoogle: async (credential: string) => {
        set({ isLoading: true });
        try {
          const response = await apiClient.post<ApiResponse<{ user: User; accessToken: string }>>(
            '/auth/google',
            { credential }
          );

          const { user, accessToken } = response.data.data;

          localStorage.setItem('authToken', accessToken);
          localStorage.setItem('user', JSON.stringify(user));

          set({
            user,
            token: accessToken,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
      },

      setUser: (user: User, token: string) => {
        localStorage.setItem('authToken', token);
        localStorage.setItem('user', JSON.stringify(user));
        set({
          user,
          token,
          isAuthenticated: true,
        });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
